import { createFileRoute } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CustomSelect } from "@/components/ui/custom-select";
import { ErrorBanner } from "@/components/ui/error-banner";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { PageTitle } from "@/contexts/page-title-context";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Text, EmptyState } from "@/components/ui/text";
import { ResizeHandle } from "@/components/ui/resize-handle";
import {
  SortableHeaderCell,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { TableViewport } from "@/components/ui/table-viewport";
import {
  ResizableTableShell,
  type ColumnDef,
} from "@/components/ui/resizable-table-shell";
import { CompactCardSkeleton } from "@/components/ui/compact-card-skeleton";
import { HStack } from "@/components/ui/h-stack";
import { Icon } from "@/components/ui/icons";
import { StickyHeaderSlot } from "@/components/ui/sticky-header-slot";
import { VStack } from "@/components/ui/v-stack";
import { DividedList } from "@/components/ui/divided-list";
import { useSizeClass } from "@/hooks/use-size-class";
import { useSortableTable } from "@/hooks/use-sortable-table";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/lib/formatting";
import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import { apiClient, ApiError } from "@/lib/api/client";
import { useToast } from "@/components/ui/toast";
import type { TicketStatus, TicketPriority } from "@/lib/api/enums";
import type { Ticket } from "@/lib/api/tickets";
import {
  claimTicket,
  releaseTicket,
  TICKET_STATUSES,
  TICKET_PRIORITIES,
  TICKET_TYPE_PREFIX_MAP,
} from "@/lib/api/tickets";
import { getCurrentUser } from "@/lib/api/auth";
import { fromIntlLanguage } from "@/lib/i18n/config";
import { highlight, highlightNumber } from "@/lib/highlight";
import { ticketSearchableFields } from "@/lib/search/ticket";

type FilterTab = "all" | "mine" | "unassigned";

function AdminSupportTicketsPage() {
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const isRegular = sizeClass === "regular";
  const navigate = useNavigate();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tAdminSupportTickets } = useTranslation("admin.supportTickets");
  const { t: tCommon } = useTranslation("common");
  const { t: tTickets } = useTranslation("tickets");
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const {
    filters,
    setFilter,
    clearFilters: clearAllFilters,
  } = useFilterSearchParams({
    keys: ["search", "tab", "status", "priority"],
    defaults: { tab: "all" },
  });
  const activeTab = (filters.tab || "all") as FilterTab;
  const statusFilter = (TICKET_STATUSES as readonly string[]).includes(
    filters.status,
  )
    ? (filters.status as TicketStatus)
    : "";
  const priorityFilter = (TICKET_PRIORITIES as readonly string[]).includes(
    filters.priority,
  )
    ? (filters.priority as TicketPriority)
    : "";
  const searchQuery = filters.search;

  // FilterToolbar already debounces draft → URL commits at 300ms, so
  // searchQuery here is the committed (debounced) value.
  const q = useMemo(() => {
    const trimmed = searchQuery.trim();
    return trimmed.length >= 3 ? trimmed : undefined;
  }, [searchQuery]);

  const ticketQueryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (statusFilter) params.status = statusFilter;
    if (priorityFilter) params.priority = priorityFilter;
    if (activeTab === "unassigned") params.scope = "unassigned";
    if (q) params.q = q;
    return params;
  }, [statusFilter, priorityFilter, activeTab, q]);

  const ticketsQuery = useQuery({
    queryKey: queryKeys.admin.tickets(ticketQueryParams),
    queryFn: () => {
      const qs = new URLSearchParams(ticketQueryParams);
      return apiClient.get<Ticket[]>(`/tickets?${qs.toString()}`);
    },
  });

  const tickets = useMemo(
    () => (Array.isArray(ticketsQuery.data) ? ticketsQuery.data : []),
    [ticketsQuery.data],
  );
  const loading = ticketsQuery.isLoading;
  const errorKind = ticketsQuery.isError ? ("load" as const) : null;

  const invalidateTickets = () => {
    void queryClient.invalidateQueries({
      queryKey: ["admin", "tickets"],
    });
  };

  const handleClaim = async (ticketId: string) => {
    try {
      await claimTicket(ticketId);
      invalidateTickets();
    } catch (err) {
      console.error("Error claiming ticket:", err);
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === "TICKET_ALREADY_CLAIMED") {
        addToast(tTickets("errors.alreadyClaimed"), "error");
      } else {
        addToast(tTickets("errors.claimFailed"), "error");
      }
    }
  };

  const handleRelease = async (ticketId: string) => {
    try {
      await releaseTicket(ticketId);
      invalidateTickets();
    } catch (err) {
      console.error("Error releasing ticket:", err);
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === "NOT_CLAIMED_BY_USER") {
        addToast(tTickets("errors.notClaimedByYou"), "error");
      } else {
        addToast(tTickets("errors.releaseFailed"), "error");
      }
    }
  };

  const currentUserId = getCurrentUser()?.id;

  const tabs: { key: FilterTab; label: string }[] = [
    { key: "all", label: tAdminSupportTickets("tabAll") },
    { key: "mine", label: tAdminSupportTickets("tabMine") },
    { key: "unassigned", label: tAdminSupportTickets("tabUnassigned") },
  ];

  const clearFilters = () => {
    setFilter("status", "");
    setFilter("priority", "");
  };

  const handleTabChange = (tab: FilterTab) => {
    clearAllFilters();
    setFilter("tab", tab);
  };

  // ─── Resizable columns ──────────────────────────────────────────────
  const userId = getCurrentUser()?.id ?? null;
  const columnDefs = useMemo((): ColumnDef[] => {
    const allCols: ColumnDef[] = [
      { key: "ticket_number", defaultWidth: 80, minWidth: 60 },
      { key: "type", defaultWidth: 100, minWidth: 60 },
      { key: "name", defaultWidth: 200, minWidth: 100 },
      { key: "status", defaultWidth: 120, minWidth: 80 },
      { key: "priority", defaultWidth: 120, minWidth: 80 },
      { key: "assignee", defaultWidth: 160, minWidth: 80 },
      { key: "created", defaultWidth: 140, minWidth: 80 },
      { key: "actions", defaultWidth: 120, minWidth: 80 },
    ];
    if (isRegular)
      return allCols.filter((c) => !["type", "created"].includes(c.key));
    return allCols;
  }, [isRegular]);

  const priorityColor: Record<
    string,
    "neutral" | "info" | "warning" | "danger"
  > = {
    Low: "neutral",
    Normal: "info",
    High: "warning",
    Urgent: "danger",
  };

  // "mine" filter: client-side filter by current user's assignment.
  // Search narrows server-side via ?q= — no client filter.
  const filteredByTab = useMemo(() => {
    if (activeTab === "mine" && currentUserId) {
      return tickets.filter((t) => t.assignee?.id === currentUserId);
    }
    return tickets;
  }, [tickets, activeTab, currentUserId]);

  const {
    sort,
    toggleSort,
    sortedData: sortedTickets,
  } = useSortableTable(filteredByTab, { defaultColumn: "priority" });

  // Pagination — use filter key to reset page via React key pattern
  const PAGE_SIZE = 20;
  const filterKey = `${activeTab}|${statusFilter}|${priorityFilter}|${searchQuery}`;
  const [pageState, setPageState] = useState({ key: filterKey, page: 1 });
  // Reset page when filters change
  const ticketPage = pageState.key === filterKey ? pageState.page : 1;
  const setTicketPage = (p: number) =>
    setPageState({ key: filterKey, page: p });
  const ticketTotalPages = Math.max(
    1,
    Math.ceil(sortedTickets.length / PAGE_SIZE),
  );
  const effectivePage = Math.min(ticketPage, ticketTotalPages);
  const paginatedTickets = useMemo(
    () =>
      sortedTickets.slice(
        (effectivePage - 1) * PAGE_SIZE,
        effectivePage * PAGE_SIZE,
      ),
    [sortedTickets, effectivePage],
  );

  const statusColor: Record<string, "success" | "info" | "neutral" | "danger"> =
    {
      Open: "success",
      InProgress: "info",
      Resolved: "neutral",
      Cancelled: "danger",
    };

  return (
    <VStack gap="xl" data-testid="admin-support-tickets-page">
      <PageTitle>{tAdminSupportTickets("title")}</PageTitle>

      <StickyHeaderSlot>
        <Card variant="bordered" padding="xs">
          <FilterToolbar
            searchPlaceholder={tCommon("search")}
            searchValue={searchQuery}
            onSearchChange={(v) => setFilter("search", v)}
            pagination={{
              page: effectivePage,
              totalPages: ticketTotalPages,
              perPage: PAGE_SIZE,
              onPageChange: setTicketPage,
              loading,
            }}
            tabs={
              <SegmentedControl
                options={tabs.map((tab) => ({
                  value: tab.key,
                  label: tab.label,
                }))}
                value={activeTab}
                onChange={(value) => handleTabChange(value as FilterTab)}
                data-testid="support-tickets-tabs"
              />
            }
            advancedFilters={[
              <CustomSelect
                key="status-filter"
                value={statusFilter}
                onChange={(value) => setFilter("status", value)}
                data-testid="status-filter"
                emptyLabel="filter"
                options={TICKET_STATUSES.map((s) => ({ value: s, label: s }))}
                label={tTickets("status")}
              />,
              <CustomSelect
                key="priority-filter"
                value={priorityFilter}
                onChange={(value) => setFilter("priority", value)}
                data-testid="priority-filter"
                emptyLabel="filter"
                options={TICKET_PRIORITIES.map((p) => ({ value: p, label: p }))}
                label={tTickets("priority")}
              />,
            ]}
            activeFilterCount={
              (statusFilter ? 1 : 0) + (priorityFilter ? 1 : 0)
            }
            onClearFilters={clearFilters}
            data-testid="admin-support-tickets-toolbar"
          />
        </Card>
      </StickyHeaderSlot>

      {errorKind && (
        <ErrorBanner
          title={tCommon("errorLoadingData")}
          onRetry={() => void ticketsQuery.refetch()}
          retryLabel={tCommon("retry")}
        />
      )}

      {/* Tickets table */}
      {loading ? (
        <Card variant="bordered" padding="none">
          {isCompact ? (
            <CompactCardSkeleton />
          ) : (
            <TableSkeleton columns={9} rows={5} />
          )}
        </Card>
      ) : paginatedTickets.length === 0 ? (
        <Card variant="bordered" padding="none">
          <EmptyState
            icon={<Icon.Search size="xl" />}
            title={tAdminSupportTickets("noTickets")}
            className="py-16"
          />
        </Card>
      ) : isCompact ? (
        <Card variant="bordered" padding="none">
          <DividedList>
            {paginatedTickets.map((ticket) => {
              const isMine = ticket.assignee?.id === userId;
              const fields = ticketSearchableFields(ticket, language);
              return (
                <VStack
                  gap="sm"
                  key={ticket.id}
                  data-testid={`ticket-card-${ticket.id}`}
                  className="px-4 py-3 cursor-pointer transition-colors active:bg-[color:var(--surface-container)]"
                  onClick={() =>
                    void navigate({ to: `/admin/support-tickets/${ticket.id}` })
                  }
                >
                  <HStack justify="between" gap="none">
                    <Text size="xs" color="muted" className="font-mono">
                      {fields.ticket_number.value
                        ? highlightNumber(fields.ticket_number, searchQuery)
                        : "—"}
                      {ticket.type
                        ? ` · ${TICKET_TYPE_PREFIX_MAP[ticket.type] ?? ticket.type}`
                        : ""}
                    </Text>
                    <HStack align="stretch" gap="sm">
                      {searchQuery && fields.type.value ? (
                        <Text size="xs" color="muted">
                          {highlight(fields.type, searchQuery)}
                        </Text>
                      ) : null}
                      <Badge color={statusColor[ticket.status] ?? "neutral"}>
                        {highlight(fields.status, searchQuery)}
                      </Badge>
                    </HStack>
                  </HStack>
                  <Text weight="semibold">
                    {fields.title.value
                      ? highlight(fields.title, searchQuery)
                      : "—"}
                  </Text>
                  <HStack>
                    <Badge color={priorityColor[ticket.priority] ?? "neutral"}>
                      {highlight(fields.priority, searchQuery)}
                    </Badge>
                    <Text size="xs" color="muted">
                      {ticket.assignee?.name ?? tTickets("unassigned")}
                    </Text>
                  </HStack>
                  <HStack align="stretch">
                    {!isMine ? (
                      <Button
                        size="xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClaim(ticket.id);
                        }}
                        data-testid={`claim-${ticket.id}`}
                      >
                        {tTickets("claim")}
                      </Button>
                    ) : (
                      <Button
                        color="neutral"
                        variant="outline"
                        size="xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRelease(ticket.id);
                        }}
                        data-testid={`release-${ticket.id}`}
                      >
                        {tTickets("release")}
                      </Button>
                    )}
                  </HStack>
                </VStack>
              );
            })}
          </DividedList>
        </Card>
      ) : (
        <TableViewport>
          <ResizableTableShell
            tableId="admin-support-tickets"
            userId={userId}
            columns={columnDefs}
            className="text-sm"
            data-testid="tickets-table"
          >
            {({ onResizeStart }) => (
              <>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableHeaderCell className="relative" dense>
                      #
                      <ResizeHandle
                        columnKey="ticket_number"
                        onResizeStart={onResizeStart}
                      />
                    </TableHeaderCell>
                    {!isRegular && (
                      <TableHeaderCell className="relative" dense>
                        {tTickets("type")}
                        <ResizeHandle
                          columnKey="type"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                    )}
                    <TableHeaderCell className="relative" dense>
                      {tCommon("name")}
                      <ResizeHandle
                        columnKey="name"
                        onResizeStart={onResizeStart}
                      />
                    </TableHeaderCell>
                    <SortableHeaderCell
                      column="status"
                      sortColumn={sort?.column}
                      sortDirection={sort?.direction}
                      onSort={toggleSort}
                      className="relative"
                      dense
                    >
                      {tTickets("status")}
                      <ResizeHandle
                        columnKey="status"
                        onResizeStart={onResizeStart}
                      />
                    </SortableHeaderCell>
                    <SortableHeaderCell
                      column="priority"
                      sortColumn={sort?.column}
                      sortDirection={sort?.direction}
                      onSort={toggleSort}
                      className="relative"
                      dense
                    >
                      {tTickets("priority")}
                      <ResizeHandle
                        columnKey="priority"
                        onResizeStart={onResizeStart}
                      />
                    </SortableHeaderCell>
                    <TableHeaderCell className="relative" dense>
                      {tTickets("assignee")}
                      <ResizeHandle
                        columnKey="assignee"
                        onResizeStart={onResizeStart}
                      />
                    </TableHeaderCell>
                    {!isRegular && (
                      <SortableHeaderCell
                        column="created_at"
                        sortColumn={sort?.column}
                        sortDirection={sort?.direction}
                        onSort={toggleSort}
                        className="relative"
                        dense
                      >
                        {tTickets("created")}
                        <ResizeHandle
                          columnKey="created"
                          onResizeStart={onResizeStart}
                        />
                      </SortableHeaderCell>
                    )}
                    <TableHeaderCell className="relative" dense sticky>
                      {tCommon("actions")}
                      <ResizeHandle
                        columnKey="actions"
                        onResizeStart={onResizeStart}
                      />
                    </TableHeaderCell>
                    <TableHeaderCell className="w-auto" dense />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTickets.map((ticket) => {
                    const fields = ticketSearchableFields(ticket, language);
                    return (
                      <TableRow
                        key={ticket.id}
                        clickable
                        onClick={() =>
                          void navigate({
                            to: `/admin/support-tickets/${ticket.id}`,
                          })
                        }
                      >
                        <TableCell className="font-mono text-xs" dense>
                          {fields.ticket_number.value
                            ? highlightNumber(fields.ticket_number, searchQuery)
                            : "—"}
                        </TableCell>
                        {!isRegular && (
                          <TableCell className="text-xs" dense>
                            <HStack align="stretch" gap="sm">
                              {ticket.type
                                ? (TICKET_TYPE_PREFIX_MAP[ticket.type] ??
                                  ticket.type)
                                : "—"}
                              {searchQuery && fields.type.value ? (
                                <Text size="xs" color="muted">
                                  {highlight(fields.type, searchQuery)}
                                </Text>
                              ) : null}
                            </HStack>
                          </TableCell>
                        )}
                        <TableCell dense>
                          {fields.title.value
                            ? highlight(fields.title, searchQuery)
                            : "—"}
                        </TableCell>
                        <TableCell dense>
                          <Badge
                            color={statusColor[ticket.status] ?? "neutral"}
                          >
                            {highlight(fields.status, searchQuery)}
                          </Badge>
                        </TableCell>
                        <TableCell dense>
                          <Badge
                            color={priorityColor[ticket.priority] ?? "neutral"}
                          >
                            {highlight(fields.priority, searchQuery)}
                          </Badge>
                        </TableCell>
                        <TableCell dense>
                          {ticket.assignee?.name ?? tTickets("unassigned")}
                        </TableCell>
                        {!isRegular && (
                          <TableCell
                            dense
                            className="text-[color:var(--on-surface-variant)]"
                          >
                            {ticket.created_at
                              ? formatDate(ticket.created_at, language)
                              : "—"}
                          </TableCell>
                        )}
                        <TableCell dense sticky>
                          <HStack align="stretch">
                            {!ticket.assignee ? (
                              <Button
                                size="xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClaim(ticket.id);
                                }}
                                data-testid={`claim-${ticket.id}`}
                              >
                                {tTickets("claim")}
                              </Button>
                            ) : (
                              <Button
                                color="neutral"
                                variant="outline"
                                size="xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRelease(ticket.id);
                                }}
                                data-testid={`release-${ticket.id}`}
                              >
                                {tTickets("release")}
                              </Button>
                            )}
                          </HStack>
                        </TableCell>
                        <TableCell dense />
                      </TableRow>
                    );
                  })}
                </TableBody>
              </>
            )}
          </ResizableTableShell>
        </TableViewport>
      )}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/support-tickets")({
  component: AdminSupportTicketsPage,
});
